import { Field, Select } from '@buildmate/ui-web';

/**
 * The fields of a job, shared by the create and edit dialogs.
 *
 * One component rather than two forms, because the two drifted the moment
 * they existed separately: creation offered `lead`/`open`/`closed` but not
 * `archived`, and neither offered a projected end date the schema has
 * always had. A field added here appears in both places, which is the only
 * arrangement where "edit what you created" stays true.
 */
export interface JobFormValues {
  name: string;
  prefix: string;
  status: string;
  line1: string;
  city: string;
  state: string;
  postalCode: string;
  startDate: string;
  projectedEndDate: string;
  actualEndDate: string;
  contractType: string;
}

export const EMPTY_JOB_FORM: JobFormValues = {
  name: '',
  prefix: '',
  status: 'open',
  line1: '',
  city: '',
  state: '',
  postalCode: '',
  startDate: '',
  projectedEndDate: '',
  actualEndDate: '',
  contractType: 'fixed',
};

interface Props {
  readonly values: JobFormValues;
  readonly onChange: (key: keyof JobFormValues) => (event: { target: { value: string } }) => void;
  readonly fieldErrors: Record<string, string>;
  /**
   * Creating offers a prefix; editing shows it and will not take a new one.
   *
   * The prefix is printed into every purchase order and invoice number on
   * the job, and a supplier is holding that reference. The security rules
   * refuse a change, so a writable field here would be a box that silently
   * fails — better to show the value and say why it is fixed.
   */
  readonly prefix: 'editable' | 'fixed';
  /** Archiving is an end state, so it is only reachable once a job exists. */
  readonly allowArchived?: boolean;
}

export function JobFields({ values, onChange, fieldErrors, prefix, allowArchived }: Props) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Job name"
          data-autofocus
          value={values.name}
          onChange={onChange('name')}
          error={fieldErrors.name}
          required
        />
        {prefix === 'editable' ? (
          <Field
            label="Prefix"
            hint="Used to number documents, e.g. GLN-0001"
            value={values.prefix}
            onChange={onChange('prefix')}
            error={fieldErrors.prefix}
            required
          />
        ) : (
          <div>
            <p className="text-sm font-medium text-slate-700">Prefix</p>
            <p className="mt-2 font-mono text-sm text-slate-900">{values.prefix}</p>
            <p className="mt-1 text-xs text-slate-500">
              Fixed once the job exists — it is printed in every purchase order and invoice number.
            </p>
          </div>
        )}
      </div>

      <Field
        label="Street address"
        value={values.line1}
        onChange={onChange('line1')}
        error={fieldErrors['address.line1']}
        required
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <Field
          label="City"
          value={values.city}
          onChange={onChange('city')}
          error={fieldErrors['address.city']}
          required
        />
        <Field
          label="State"
          value={values.state}
          onChange={onChange('state')}
          error={fieldErrors['address.state']}
          required
        />
        <Field
          label="Postal code"
          value={values.postalCode}
          onChange={onChange('postalCode')}
          error={fieldErrors['address.postalCode']}
          required
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label="Status"
          value={values.status}
          onChange={onChange('status')}
          options={[
            { value: 'lead', label: 'Lead' },
            { value: 'open', label: 'Open' },
            { value: 'closed', label: 'Closed' },
            ...(allowArchived ? [{ value: 'archived', label: 'Archived' }] : []),
          ]}
        />
        <Select
          label="Contract"
          value={values.contractType}
          onChange={onChange('contractType')}
          options={[
            { value: 'fixed', label: 'Fixed price' },
            { value: 'openBook', label: 'Open book' },
          ]}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Field
          label="Start date"
          type="date"
          value={values.startDate}
          onChange={onChange('startDate')}
          error={fieldErrors.startDate}
        />
        <Field
          label="Projected end"
          type="date"
          value={values.projectedEndDate}
          onChange={onChange('projectedEndDate')}
          error={fieldErrors.projectedEndDate}
        />
        <Field
          label="Actual end"
          type="date"
          value={values.actualEndDate}
          onChange={onChange('actualEndDate')}
          error={fieldErrors.actualEndDate}
        />
      </div>
    </>
  );
}
